import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { ConfigProvider, ConfigProviderProps, theme } from "antd";
import useOrg from "../hooks/apis/useOrg";
import FA_IR from "antd/locale/fa_IR";
import { OrgTheme } from "../types";
import Loading from "../components/loading";
import Error from "../components/error";
import { Helmet } from "react-helmet";
import { IS_WHITE_LABEL } from "../constants";

const ThemeContext = createContext<{
  isDark: boolean;
  toggleTheme: () => void;
  orgTheme: OrgTheme | null;
}>({
  isDark: false,
  toggleTheme: () => {},
  orgTheme: null,
});

const DEFAULT_FONT = "YekanBakh, Vazirmatn, IRANSans, 'Segoe UI', Tahoma, -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', Arial, sans-serif";
const WHITE_LABEL_FONT = "'Noto Sans Arabic Variable', 'Noto Sans Arabic', sans-serif";

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading, error } = useOrg();
  const orgTheme = data?.theme || null;
  const brandTitle = orgTheme?.title || data?.title || "پنل کاربری";
  const isWhiteLabel = data?.config?.whiteLabel ?? IS_WHITE_LABEL;

  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return (
      savedTheme === "dark" ||
      (!savedTheme && window.matchMedia("(prefers-color-scheme: dark)").matches)
    );
  });

  useEffect(() => {
    if (!orgTheme) return;
    const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const manifestUrl = URL.createObjectURL(new Blob([JSON.stringify({
      id: "/",
      name: `${brandTitle} | پنل کاربری`,
      short_name: brandTitle,
      description: `پنل کاربری ${brandTitle}`,
      lang: "fa-IR",
      dir: "rtl",
      start_url: "/",
      scope: "/",
      display: "standalone",
      theme_color: orgTheme.primaryColor || "#1677FF",
      background_color: "#F5F5F5",
      icons: orgTheme.favicon
        ? [{ src: orgTheme.favicon, sizes: "any", purpose: "any" }]
        : [],
    })], { type: "application/manifest+json" }));
    manifestLink?.setAttribute("href", manifestUrl);
    return () => URL.revokeObjectURL(manifestUrl);
  }, [brandTitle, orgTheme]);

  useEffect(() => {
    const whiteLabel = data?.config?.whiteLabel ?? IS_WHITE_LABEL;
    if (!data || whiteLabel || document.querySelector('script[data-gtm]')) return;
    const id = import.meta.env.VITE_APP_GTM_ID || "GTM-NP8J2G3";
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    const script = document.createElement("script");
    script.async = true;
    script.dataset.gtm = id;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    document.head.appendChild(script);
  }, [data]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.toggle("white-label", isWhiteLabel);
    root.style.setProperty(
      "--app-body-font-family",
      isWhiteLabel ? orgTheme?.fontFamily || WHITE_LABEL_FONT : DEFAULT_FONT,
    );
    root.style.setProperty("--app-body-font-weight", String(orgTheme?.fontWeight || 400));
    if (isDark) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDark, isWhiteLabel, orgTheme?.fontFamily, orgTheme?.fontWeight]);

  const toggleTheme = () => setIsDark(!isDark);

  const contextValue = useMemo(() => ({ isDark, toggleTheme, orgTheme }), [isDark, orgTheme]);

  if (error) {
    return (
      <Error
        size="screen"
        text="خطایی رخ داده است"
        iconClassName="text-red-900"
        textClassName="text-gray-900 mt-2 text-2xl"
      />
    );
  }

  if (isLoading || !data)
    return (
      <Loading
        text="در حال بارگذاری..."
        size="screen"
        spinnerClassName="text-gray-800"
        textClassName="text-gray-800"
      />
    );

  // Single Ant theme config, sourced from the Youber design system
  // (src/styles/tokens.css). colorPrimary stays org-driven for white-label
  // tenants and falls back to the canonical brand red.
  const BRAND_RED = "#F5392B";
  const themeConfig: ConfigProviderProps["theme"] = {
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      fontFamily:
        isWhiteLabel && orgTheme?.fontFamily
          ? orgTheme.fontFamily
          : isWhiteLabel
            ? WHITE_LABEL_FONT
            : DEFAULT_FONT,
      colorPrimary: orgTheme?.primaryColor || BRAND_RED,
      colorInfo: "#1677FF",
      colorLink: orgTheme?.secondaryColor || "#1677FF",
      colorSuccess: "#52C41A",
      colorWarning: "#FAAD14",
      colorError: "#FF4D4F",
      fontSize: isWhiteLabel ? orgTheme?.baseFontSize || 14 : 14,
      borderRadius: 6, // inputs / buttons
      borderRadiusLG: 8, // large buttons, alerts
      borderRadiusSM: 4,
      borderRadiusXS: 2,
      // Page background — slightly off-white in light, true dark in dark mode
      colorBgLayout: isDark ? "#0F0F0F" : "#F5F5F5",
    },
    components: {
      Menu: {
        // Active nav item: soft-red pill + brand-red text are derived
        // automatically from colorPrimary by the Ant algorithm.
        itemBorderRadius: 8,
        darkItemBg: "#141414",
        darkSubMenuItemBg: "#141414",
        darkPopupBg: "#141414",
      },
    },
  };

  return (
    <>
      {orgTheme ? (
        <Helmet>
          <title>{brandTitle} | پنل کاربری</title>
          <meta name="description" content={`پنل کاربری ${brandTitle}`} />
          <meta name="application-name" content={brandTitle} />
          <meta name="apple-mobile-web-app-title" content={brandTitle} />
          <meta name="theme-color" content={orgTheme.primaryColor || "#1677FF"} />
          {orgTheme.favicon ? <link rel="icon" href={orgTheme.favicon} /> : null}
          {orgTheme.favicon ? <link rel="apple-touch-icon" href={orgTheme.favicon} /> : null}
        </Helmet>
      ) : null}
      <ThemeContext.Provider value={contextValue}>
        <ConfigProvider direction="rtl" locale={FA_IR} theme={themeConfig}>
          {children}
        </ConfigProvider>
      </ThemeContext.Provider>
    </>
  );
}

export const useTheme = () => useContext(ThemeContext);
